/**
 * Bridge between the app-wide Pusher socket (usePusher) and the MessagingContext.
 *
 * usePusher owns the single Pusher connection and subscribes the per-salon
 * `-messages` channel alongside `-notifications`. Rather than coupling that hook
 * to MessagingContext (which may be mounted below it), it re-emits each chat
 * event as a window CustomEvent; MessagingContext listens. This keeps one
 * socket, leaves the notifications path untouched, and avoids provider-ordering
 * constraints.
 */
import type { Message } from "../types/messaging";

export const MESSAGING_REALTIME_EVENT = "pagoda:messaging";

/** Pusher event names published on the `-messages` channel (see bookings-infra). */
export type MessagingRealtimeName =
  | "message.received"
  | "message.created"
  | "message.status";

export interface MessagingRealtimePayload {
  name: MessagingRealtimeName;
  data: {
    conversation_key?: string;
    conversation_id?: string;
    business_location_uuid?: string;
    message_uuid?: string;
    direction?: Message["direction"];
    /** Wire value; `message.status` may carry states beyond `MessageStatus`. */
    status?: string;
    /** `message.status` only — raw Twilio error code for a failed send. */
    error_code?: string | null;
    /** `message.status` only — the outbound message's Twilio SID. */
    twilio_message_sid?: string | null;
    body?: string;
    has_media?: boolean;
    created_at?: string;
  };
}

/** Re-emit a chat event from usePusher onto the window (consumed by the context). */
export function emitMessagingRealtime(payload: MessagingRealtimePayload): void {
  window.dispatchEvent(
    new CustomEvent<MessagingRealtimePayload>(MESSAGING_REALTIME_EVENT, {
      detail: payload,
    }),
  );
}
